import { themeColorCss, themeFontCss } from '@sites/site-theme/src/utils/variables';
import { ThemeFontTags } from '@sites/site-theme/src/enums/font-tags.enum';
import { ThemeFontProperties } from '@sites/site-theme/src/enums/font-properties.enum';
import { ThemeColorNames } from '@sites/site-theme/src/enums/color-names.enum';

import type { CssVars, ThemedButtonLike } from '@sites/entity-core';
import type { FormRootThemeTokens } from '@sites/entity-core/src/widget/form/styles/form.css-vars';
import { getThemedButtonCssVars as getThemedButtonCssVarsCore } from '@sites/entity-core/src/widget/form/styles/themed-button.css-vars';

export { getThemedButtonHoverClasses } from '@sites/entity-core/src/widget/form/styles/themed-button.css-vars';
export type { CssVars, ThemedButtonLike } from '@sites/entity-core';

// Defined in `@sites/entity-core` beside the widget style twins that consume it;
// re-exported here for this file's existing consumers.
export { JUSTIFY_BY_ALIGN } from '@sites/entity-core/src/widget/common/styles/css-vars';

/**
 * The theme system stores colours as `string | string[]`; take the first stop
 * and resolve it through the website theme's colour helper. Returns `null` when
 * no colour is set so callers can omit the variable. Shared by the form,
 * payment-form, and social css-vars builders.
 */
export function colorCss(value: string | string[] | undefined): string | null {
    const color = Array.isArray(value) ? value[0] : value;

    return color ? themeColorCss(color) : null;
}

/**
 * Custom colour / border / radius / hover variables for a themed button, under
 * the given `--<prefix>-*` namespace.
 *
 * The implementation lives in `@sites/entity-core`
 * (`widget/form/styles/themed-button.css-vars.ts`) so the form, payment-form and
 * login-edu widgets share one definition — paired with the
 * `.themed-button-custom(@prefix)` LESS mixin next to it. The package is
 * framework-free and cannot reach `@sites/site-theme`, so it takes the colour
 * resolver as an argument; this wrapper binds {@link colorCss} once for the app.
 */
export function getThemedButtonCssVars(button: ThemedButtonLike, prefix: string): CssVars {
    return getThemedButtonCssVarsCore(button, prefix, colorCss);
}

/**
 * Theme values the form-family roots (form, payment-form) bake into their
 * variable bags. Resolved here because `@sites/entity-core` is framework-free and
 * cannot reach `@sites/site-theme`; both apps pass this constant in.
 */
export const FORM_ROOT_THEME_TOKENS: FormRootThemeTokens = {
    fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
    dividerColor: themeColorCss(ThemeColorNames.primary),
};
